import FingerprintJS from '@fingerprintjs/fingerprintjs';

/**
 * Browser fingerprinting (FingerprintJS, open source).
 *
 * The server uses this as an extra handle for banning anonymous users: a
 * nickname is changed in seconds and an IP is shared by everyone behind a NAT,
 * whereas a fingerprint survives a new nickname, a new tab and cleared storage.
 *
 * Only a visitor id and a few coarse signals are sent. The server stores a
 * salted hash, never the raw value.
 */

let pending = null;

/** A handful of stable, low-sensitivity signals for operator diagnostics. */
function summarise(components) {
  const value = (name) => components?.[name]?.value;
  const screenResolution = value('screenResolution');
  return {
    platform: String(value('platform') ?? 'unknown').slice(0, 40),
    timezone: String(value('timezone') ?? 'unknown').slice(0, 60),
    languages: JSON.stringify(value('languages') ?? []).slice(0, 60),
    screen: Array.isArray(screenResolution) ? screenResolution.join('x') : 'unknown',
    colorDepth: String(value('colorDepth') ?? ''),
    cores: String(value('hardwareConcurrency') ?? ''),
    touch: String(value('touchSupport')?.maxTouchPoints ?? ''),
  };
}

/**
 * Computes the fingerprint once per page load and caches the promise.
 * Resolves to null when fingerprinting is unavailable (privacy mode, blocked
 * script, unsupported browser) - the server decides whether that is acceptable.
 */
export function collectFingerprint() {
  if (pending) return pending;
  pending = (async () => {
    try {
      const agent = await FingerprintJS.load({ debug: false });
      const result = await agent.get();
      return {
        visitorId: result.visitorId,
        confidence: result.confidence?.score ?? null,
        signals: summarise(result.components),
      };
    } catch (err) {
      console.warn('fingerprint unavailable', err);
      return null;
    }
  })();
  return pending;
}

/**
 * Waits up to `timeoutMs` for the fingerprint, so a slow or blocked computation
 * never holds up joining. Returns null when it is not ready in time; the caller
 * can send it later over `session.fingerprint`.
 */
export function fingerprintWithin(timeoutMs = 1500) {
  return Promise.race([
    collectFingerprint(),
    new Promise((resolve) => setTimeout(() => resolve(null), timeoutMs)),
  ]);
}

export default collectFingerprint;
